import { useEffect, useMemo, useState } from 'react';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { useGetCourtsCalendar } from '../../court-details/hooks/useGetCourtCalendar';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { CourtDto, SportType } from '../../../models/court/dto/court.dto';
import { GameDto, GameStatus } from '../../../models/game/dto/game.dto';
import { CoachingSessionDto, CoachingSessionStatus } from '../../../models/coach/dto/coaching-session.dto';
import { CourtTemporaryBlockadeDto, CourtTemporaryBlockadeReason } from '../../../models/court/dto/court-temporary-blockade.dto';
import { EventDto, EventStatus } from '../../../models/event/dto/event.dto';
import { translate } from '../../../i18n';

const STICKY_THRESHOLD_MIN = 15;
const REFRESH_MS = 30_000;

export type VenueCurrentState =
    | { kind: 'live'; sport: 'singles' | 'doubles' | 'open'; players: string; endsAt: Date }
    | { kind: 'class'; coachName: string; participantCount?: number; endsAt: Date }
    | { kind: 'event'; name: string; endsAt: Date }
    | { kind: 'blocked'; reasonLabel: string; endsAt: Date }
    | { kind: 'open' }
    | { kind: 'idle' };

export type VenueNextState = {
    kind: 'game' | 'class' | 'event';
    label: string;
    sub?: string;
    startsAt: Date;
    startsInMin: number;
};

export type VenueCourtState = {
    courtId: number;
    display: { num: string; label: string }; // "1" / "Court" — or custom name → name / 'Court'
    sport?: SportType;
    current: VenueCurrentState;
    next: VenueNextState | null;
    sticky: boolean;
};

export type VenueDisplayState = {
    courts: VenueCourtState[];
    locationName: string | null;
    isLoading: boolean;
    hasError: boolean;
    /**
     * The courts read was never ISSUED — no club is selected, so this screen has
     * nothing to show and no amount of waiting will change that (run 134,
     * R110-6/R133-4). Separated from `isLoading` and `hasError` because it is
     * neither: the display used to answer it with an empty board ("no courts"),
     * and driving a loading affordance from it instead would be worse still —
     * React Query v4 reports a disabled query as `isLoading: true` with
     * `fetchStatus: 'idle'`, i.e. forever (D64).
     */
    needsLocation: boolean;
    syncedAt: Date;
};

// --- Helpers ---

function redactName(first?: string | null, last?: string | null): string {
    const f = (first ?? '').trim();
    const l = (last ?? '').trim();
    if (!f && !l) return translate('venue_display.live.unknown_player');
    if (!l) return f;
    return `${f} ${l[0]}.`;
}

function parseCourtDisplay(name: string): { num: string; label: string } {
    const label = translate('venue_display.live.court_label');
    if (!name) return { num: '?', label };
    const match = name.match(/(?:court|pista|terrain|piste|kort)\s*(\d+|[ivxlcdm]+)/i);
    if (match) return { num: match[1], label };
    const numOnly = name.match(/^\s*(\d+)\s*$/);
    if (numOnly) return { num: numOnly[1], label };
    const short = name.length <= 6 ? name : name.slice(0, 6) + '…';
    return { num: short, label };
}

function gamePairLine(game: GameDto): { players: string; sport: 'singles' | 'doubles' | 'open' } {
    const ps = (game.participants ?? []).map((p) => redactName(p.participant?.firstName, p.participant?.lastName));
    if (ps.length === 0) return { players: translate('venue_display.live.open_booking'), sport: 'open' };
    // Singles vs doubles: derived from capacity, not game.type (which is public/private).
    const isSingles = game.capacity === 2 || game.sport === SportType.PADEL1V1 || game.sport === SportType.BASKETBALL1V1;
    if (isSingles) {
        return { players: ps.length >= 2 ? `${ps[0]} vs ${ps[1]}` : ps[0], sport: 'singles' };
    }
    if (ps.length === 4) {
        return { players: `${ps[0]} & ${ps[1]} vs ${ps[2]} & ${ps[3]}`, sport: 'doubles' };
    }
    if (ps.length === 2) {
        return { players: `${ps[0]} & ${ps[1]}`, sport: 'doubles' };
    }
    return { players: `${ps[0]} · ${ps.length}/${game.capacity}`, sport: 'doubles' };
}

function gameEndsAt(game: GameDto): Date {
    const start = new Date(game.startTime);
    return new Date(start.getTime() + (game.duration ?? 60) * 60_000);
}

function blockadeLabel(b: CourtTemporaryBlockadeDto): string {
    switch (b.reason) {
        case CourtTemporaryBlockadeReason.COURT_MAINTENANCE: return translate('venue_display.live.blockade_maintenance');
        case CourtTemporaryBlockadeReason.EXTERNAL_RESERVATION: return translate('venue_display.live.blockade_reserved');
        case CourtTemporaryBlockadeReason.PENDING_PAYMENT: return translate('venue_display.live.blockade_hold');
        default: return b.description?.trim() || translate('venue_display.live.blockade_other');
    }
}

function classLabel(s: CoachingSessionDto): { coachName: string; sub?: string } {
    const coachName = s.coach?.firstName ?? translate('venue_display.live.kind_class');
    const players = s.numberOfPlayers ? `${s.numberOfPlayers}` : undefined;
    return { coachName, sub: players };
}

function isActive(start: Date, end: Date, now: Date): boolean {
    return start.getTime() <= now.getTime() && now.getTime() < end.getTime();
}

// --- Per-court state derivation ---

type CalendarBundle = {
    court: CourtDto;
    games: GameDto[];
    coachingSessions: CoachingSessionDto[];
    blockades: CourtTemporaryBlockadeDto[];
    events: EventDto[];
};

function deriveCourtState(b: CalendarBundle, now: Date): VenueCourtState {
    // Filter cancelled/completed
    const games = (b.games ?? []).filter((g) => g.status !== GameStatus.CANCELED);
    const sessions = (b.coachingSessions ?? []).filter((s) => s.status === CoachingSessionStatus.UPCOMING);
    const blockades = b.blockades ?? [];
    const events = (b.events ?? []).filter((e) => e.status !== EventStatus.CANCELLED);

    // 1) Active blockade wins over everything
    const activeBlockade = blockades.find((bl) => isActive(new Date(bl.startTime), new Date(bl.endTime), now));
    if (activeBlockade) {
        const current: VenueCurrentState = {
            kind: 'blocked',
            reasonLabel: blockadeLabel(activeBlockade),
            endsAt: new Date(activeBlockade.endTime),
        };
        return finalize(b.court, current, [...games, ...sessions, ...events], now);
    }

    // 2) Active class
    const activeClass = sessions.find((s) => isActive(new Date(s.startTime), new Date(s.endTime), now));
    if (activeClass) {
        const { coachName } = classLabel(activeClass);
        const current: VenueCurrentState = {
            kind: 'class', coachName,
            participantCount: activeClass.numberOfPlayers,
            endsAt: new Date(activeClass.endTime),
        };
        return finalize(b.court, current, [...games, ...sessions, ...events], now);
    }

    // 3) Active game
    const activeGame = games.find((g) => isActive(new Date(g.startTime), gameEndsAt(g), now));
    if (activeGame) {
        const { players, sport } = gamePairLine(activeGame);
        const current: VenueCurrentState = {
            kind: 'live', sport, players, endsAt: gameEndsAt(activeGame),
        };
        return finalize(b.court, current, [...games, ...sessions, ...events], now);
    }

    // 4) Active event (tournaments etc.)
    const activeEvent = events.find((e) => {
        const s = new Date(e.startTime);
        const en = new Date(s.getTime() + (e.duration ?? 90) * 60_000);
        return isActive(s, en, now);
    });
    if (activeEvent) {
        const endsAt = new Date(new Date(activeEvent.startTime).getTime() + (activeEvent.duration ?? 90) * 60_000);
        const current: VenueCurrentState = { kind: 'event', name: activeEvent.name, endsAt };
        return finalize(b.court, current, [...games, ...sessions, ...events], now);
    }

    // 5) Nothing active → open if there's any upcoming, else idle
    return finalize(b.court, { kind: 'open' }, [...games, ...sessions, ...events], now);
}

type Upcoming = { startsAt: Date; node: VenueNextState };

function finalize(
    court: CourtDto,
    current: VenueCurrentState,
    rest: Array<GameDto | CoachingSessionDto | EventDto>,
    now: Date,
): VenueCourtState {
    const upcomingNodes: Upcoming[] = [];

    rest.forEach((item) => {
        const s = new Date((item as any).startTime);
        if (s.getTime() <= now.getTime()) return; // strictly future
        if ('participants' in item && 'duration' in item && 'capacity' in item && 'sport' in item) {
            const g = item as GameDto;
            const { players } = gamePairLine(g);
            upcomingNodes.push({
                startsAt: s,
                node: {
                    kind: 'game',
                    label: players,
                    startsAt: s,
                    startsInMin: Math.round((s.getTime() - now.getTime()) / 60_000),
                },
            });
        } else if ('coach' in item && 'numberOfPlayers' in item) {
            const c = item as CoachingSessionDto;
            const { coachName, sub } = classLabel(c);
            upcomingNodes.push({
                startsAt: s,
                node: {
                    kind: 'class',
                    label: translate('venue_display.live.class_with_coach', { name: coachName }),
                    sub,
                    startsAt: s,
                    startsInMin: Math.round((s.getTime() - now.getTime()) / 60_000),
                },
            });
        } else if ('eventType' in item && 'name' in item) {
            const e = item as EventDto;
            upcomingNodes.push({
                startsAt: s,
                node: {
                    kind: 'event',
                    label: e.name,
                    startsAt: s,
                    startsInMin: Math.round((s.getTime() - now.getTime()) / 60_000),
                },
            });
        }
    });

    upcomingNodes.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
    const next = upcomingNodes[0]?.node ?? null;
    const sticky = !!next && next.startsInMin >= 0 && next.startsInMin < STICKY_THRESHOLD_MIN
        && (current.kind === 'open' || current.kind === 'idle');

    return {
        courtId: court.id,
        display: parseCourtDisplay(court.name),
        sport: court.sports?.[0],
        current,
        next,
        sticky,
    };
}

// --- The hook ---

export function useVenueDisplayState(): VenueDisplayState {
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError: isCourtsError, state: courtsReadState } = useGetCourts(selectedLocation?.id as string);

    // Today: midnight to midnight
    const { startOfDay, endOfDay } = useMemo(() => {
        const t = new Date();
        const start = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0);
        const end = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1, 0, 0, 0, 0);
        return { startOfDay: start, endOfDay: end };
    }, []);

    const sortedCourts = useMemo(
        () => [...(courts ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        [courts],
    );
    const courtIds = useMemo(() => sortedCourts.map((c) => c.id), [sortedCourts]);
    const queries = useGetCourtsCalendar(courtIds, startOfDay, endOfDay);

    // Tick `now` every 30s so end-time / next-up calculations re-render even when query data is unchanged.
    const [now, setNow] = useState<Date>(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), REFRESH_MS);
        return () => clearInterval(id);
    }, []);

    // `!courts` used to stand in for all three of these at once, which was
    // called out as failing SAFE by accident (R90-3) and stopped being safe the
    // moment the hook gained an `enabled:` gate: a disabled query leaves `courts`
    // undefined forever, so the screen would have said "Loading…" for the whole
    // session. Each state now has its own term, and a REFUSED courts read — which
    // `!courts` also absorbed into "loading" — reaches the error surface.
    const isLoading = queries.length === 0 ? courtsReadState === 'loading' : queries.some((q) => q.isLoading);
    const hasError = (isCourtsError && !courts) || queries.some((q) => q.isError);
    const needsLocation = courtsReadState === 'never-issued';

    const queriesUpdatedAt = queries.map((q) => q.dataUpdatedAt).join(',');
    const courtStates = useMemo<VenueCourtState[]>(() => {
        if (!sortedCourts.length) return [];
        return sortedCourts.map((court, i) => {
            const data = queries[i]?.data;
            const bundle: CalendarBundle = {
                court,
                games: data?.games ?? [],
                coachingSessions: data?.coachingSessions ?? [],
                blockades: data?.blockades ?? [],
                events: data?.events ?? [],
            };
            return deriveCourtState(bundle, now);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedCourts, queriesUpdatedAt, now]);

    return {
        courts: courtStates,
        locationName: selectedLocation?.name ?? null,
        isLoading,
        hasError,
        needsLocation,
        syncedAt: now,
    };
}
